/**
 * 详情页与首页之间的浏览器 History 同步。
 *
 * 仅记录「进入详情页 / 返回首页」两条路径，设置页、生成中页、失败页不参与 history。
 *
 * - 打开详情 → `pushState({ activeView: details, projectId })`
 * - 详情返回 / 浏览器后退 → `popstate` → 回首页并清理详情数据
 * - 浏览器前进 → `popstate` → 按 `projectId` 恢复详情页
 */
import { FJ_COMMON_VIEW, type FJCommonView } from '@src/redux/slice/FJCommonSlice';

/** 参与 history 的视图（仅首页与详情页） */
export type FJTrackedView =
    | typeof FJ_COMMON_VIEW.START_LANDING
    | typeof FJ_COMMON_VIEW.DETAILS;

/** 写入 `history.state` 的业务数据结构 */
export type FJBrowserHistoryState = {
    activeView: FJTrackedView;
    /** 详情页时附带项目 id，供浏览器前进时恢复详情 */
    projectId?: string;
};

const TRACKED_VIEWS = new Set<FJTrackedView>([FJ_COMMON_VIEW.START_LANDING, FJ_COMMON_VIEW.DETAILS]);

class FJBrowserHistoryStore {
    /** 标记本次 `activeView` 变更来自 `popstate`，hook 侧应跳过 push */
    private _isPopStateNavigation = false;
    /** 首屏是否已 `replaceState` 初始化 */
    private _isInitialized = false;
    /** 正在撤销一次被忽略的 popstate，避免递归触发 */
    private _isRevertingNavigation = false;

    /** 当前 Redux 页面是否响应浏览器前进/后退（仅首页、详情页） */
    isHistoryNavigationView(view: FJCommonView): boolean {
        return view === FJ_COMMON_VIEW.START_LANDING || view === FJ_COMMON_VIEW.DETAILS;
    }

    isTrackedHistoryView(view: unknown): view is FJTrackedView {
        return typeof view === 'string' && TRACKED_VIEWS.has(view as FJTrackedView);
    }

    /** 读取当前 history 条目中的业务 state；非本模块写入的数据返回 `null` */
    getHistoryState(): FJBrowserHistoryState | null {
        const state = window.history.state;
        if (
            state != null &&
            typeof state === 'object' &&
            this.isTrackedHistoryView((state as FJBrowserHistoryState).activeView)
        ) {
            return state as FJBrowserHistoryState;
        }
        return null;
    }

    private _buildHistoryState(activeView: FJTrackedView, projectId?: string): FJBrowserHistoryState {
        return projectId ? { activeView, projectId } : { activeView };
    }

    /**
     * 首屏初始化：用 `replaceState` 写入首页状态，不新增 history 条目。
     * 仅执行一次，重复调用无效。
     */
    init(): void {
        if (this._isInitialized) {
            return;
        }
        this._isInitialized = true;
        window.history.replaceState(
            this._buildHistoryState(FJ_COMMON_VIEW.START_LANDING),
            document.title,
            window.location.href,
        );
    }

    /** 进入详情页时调用，新增一条 history 记录 */
    pushDetails(projectId?: string): void {
        window.history.pushState(
            this._buildHistoryState(FJ_COMMON_VIEW.DETAILS, projectId),
            document.title,
            window.location.href,
        );
    }

    /** 替换当前 history 条目（用于无栈可退时的兜底同步） */
    replaceLanding(): void {
        window.history.replaceState(
            this._buildHistoryState(FJ_COMMON_VIEW.START_LANDING),
            document.title,
            window.location.href,
        );
    }

    /** 在 `popstate` 回调开头调用，标记导航来源为浏览器前进/后退 */
    markPopStateNavigation(): void {
        this._isPopStateNavigation = true;
    }

    /**
     * 消费 popstate 标志。若此前由浏览器导航触发则返回 `true`（仅消费一次）。
     * hook 在 Redux 同步 effect 中调用，用于跳过重复的 `pushState`。
     */
    consumePopStateNavigation(): boolean {
        if (!this._isPopStateNavigation) {
            return false;
        }
        this._isPopStateNavigation = false;
        return true;
    }

    /** 消费「撤销导航」标志，用于跳过 revert 触发的二次 popstate */
    consumeRevertingNavigation(): boolean {
        if (!this._isRevertingNavigation) {
            return false;
        }
        this._isRevertingNavigation = false;
        return true;
    }

    /**
     * 当前页不参与 history 时，撤销浏览器的前进/后退，使 Redux 与 history 指针保持不变。
     */
    revertIgnoredPopState(targetState: FJBrowserHistoryState | null): void {
        this._isRevertingNavigation = true;
        if (targetState?.activeView === FJ_COMMON_VIEW.DETAILS) {
            window.history.back();
            return;
        }
        window.history.forward();
    }

    /** 触发浏览器后退（会异步触发 `popstate`） */
    navigateBack(): void {
        window.history.back();
    }

    /** 是否存在可后退的历史条目 */
    canNavigateBack(): boolean {
        return window.history.length > 1;
    }

    /** 当前 history 条目是否为详情页（用于判断 in-app 返回能否走 `history.back()`） */
    isCurrentDetails(): boolean {
        return this.getHistoryState()?.activeView === FJ_COMMON_VIEW.DETAILS;
    }
}

const browserHistoryStore = new FJBrowserHistoryStore();

export default browserHistoryStore;
